import React, { useState, useRef, useEffect, useCallback } from "react";
import { authFetch } from "../api";

/**
 * Async building typeahead. Searches our buildings (GET /buildings/?search=) and stores
 * the selected building IDs (realplus_properties.id) — which the matcher compares against
 * a listing's property_id. Multi-select; selected buildings render as removable chips.
 *
 * Props:
 *   selected    - array of building ids (numbers)
 *   onChange    - (ids[]) => void
 *   label       - field label (null to hide)
 *   placeholder - search box placeholder
 *   accent      - chip accent: "neutral" (default) or "danger" (for excluded buildings)
 *
 * Labels for already-selected ids are resolved lazily: as the user picks from results we
 * cache id→address; ids selected in a prior session are resolved on mount via a lookup.
 */
export default function BuildingSelect({ selected = [], onChange, label = "Buildings", placeholder = "Search buildings…", accent = "neutral" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState({}); // id -> display address
  const boxRef = useRef(null);

  const labelFor = (b) => b.display_address || b.building_name || b.full_address || `Building #${b.id}`;

  // Resolve labels for any pre-selected ids we don't have cached yet (edit flow).
  useEffect(() => {
    const missing = selected.filter((id) => !(id in labels));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map((id) => authFetch(`/buildings/${id}`).then((b) => [id, b ? labelFor(b) : `Building #${id}`]).catch(() => [id, `Building #${id}`]))
    ).then((pairs) => {
      if (!cancelled) setLabels((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Debounced search.
  const runSearch = useCallback((q) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    authFetch(`/buildings/?search=${encodeURIComponent(q.trim())}&limit=8`)
      .then((r) => setResults(r?.buildings || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = (b) => {
    if (!selected.includes(b.id)) {
      setLabels((prev) => ({ ...prev, [b.id]: labelFor(b) }));
      onChange([...selected, b.id]);
    }
    setQuery("");
    setResults([]);
  };
  const remove = (id) => onChange(selected.filter((x) => x !== id));

  const chipColor = accent === "danger"
    ? { bg: "#fdecea", border: "#f5c6c0", text: "#a5281b" }
    : { bg: "#e3f2fd", border: "#bbdefb", text: "#1565c0" };

  const inputStyle = {
    width: "100%", padding: "9px 10px", border: "1.5px solid #dfe3e8", borderRadius: "6px",
    fontSize: "14px", boxSizing: "border-box", backgroundColor: "white",
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      {label && (
        <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#333", marginBottom: "6px" }}>{label}</label>
      )}

      {/* Selected chips */}
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
          {selected.map((id) => (
            <span key={id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12.5px",
              backgroundColor: chipColor.bg, border: `1px solid ${chipColor.border}`, color: chipColor.text,
              borderRadius: "16px", padding: "4px 6px 4px 11px",
            }}>
              {labels[id] || `Building #${id}`}
              <button type="button" onClick={() => remove(id)} aria-label="Remove"
                style={{ border: "none", background: "none", color: chipColor.text, cursor: "pointer", fontSize: "15px", lineHeight: 1, padding: "0 2px" }}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* Search box */}
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />

      {/* Results dropdown */}
      {open && query.trim().length >= 2 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", backgroundColor: "white",
          border: "1px solid #ced4da", borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1000, maxHeight: "280px", overflowY: "auto",
        }}>
          {loading && <div style={{ padding: "12px", color: "#999", fontSize: "13px" }}>Searching…</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: "12px", color: "#999", fontSize: "13px" }}>No buildings found in our data.</div>
          )}
          {!loading && results.map((b) => {
            const chosen = selected.includes(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => add(b)}
                disabled={chosen}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", textAlign: "left",
                  padding: "9px 12px", border: "none", borderBottom: "1px solid #f0f0f0",
                  backgroundColor: chosen ? "#f5f7fa" : "white", cursor: chosen ? "default" : "pointer",
                  color: chosen ? "#9aa3ad" : "#333",
                }}
              >
                <span style={{ fontSize: "13.5px", fontWeight: 600 }}>{labelFor(b)} {chosen && "✓"}</span>
                {(b.neighborhood || b.building_name) && (
                  <span style={{ fontSize: "11.5px", color: "#8a929b" }}>
                    {[b.building_name && b.building_name !== b.display_address ? b.building_name : null, b.neighborhood].filter(Boolean).join(" · ")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
